import { useFormik } from "formik";
import { ContentDetailType, ContentType, UserCoverStyle } from "@authory-packages/core";
import { useEffect, useState, type JSX } from "react";
import { format } from "date-fns";
import * as Yup from "yup";
import { V3PanelWithPreview } from "../V3PanelWithPreview/V3PanelWithPreview";
import { renderArticleDate } from "../../utils/articleDateParser";
import { YupDateValidation, YupNameValidation, YupSourceValidation } from "../../shared-logic/validation-utils";
import { V3Button, V3ButtonColor, V3TertiaryButton } from "../Button";
import { V3IconHelpCircle, V3IconRefresh } from "../Icons";
import { VerticalSpacing } from "../VerticalSpacing/VerticalSpacing";
import { V3TextArea, V3TextInput } from "../TextInput/V3TextInput";
import { NotificationInlineLink } from "../ReusableStyledComponents/NotificationInlineLink";
import { FFNoticeTooltip, ItemPreviewBtnWrapper, ItemPreviewDateInputWrapper, ItemPreviewSourceDateWrapper } from "./ItemPreviewEdit.styles";
import { V3Loader } from "../Loader";
import { MediaUploadWrapper } from "../MediaUploadWrapper";
import { V3TertiaryCTAColor } from "../Button/V3TertiaryCTA.types";
import { MAX_DESCRIPTION_SIZE } from "../../types/max_description_size";
import { V3Title } from "../Typography";
import { SourcePicker, SourcePickerFormik } from "../SourcePicker";
import { RightAlignPanel } from "../ReusableStyledComponents";
import { BannerDetails } from "../BannerDetails";
import { V3Tooltip } from "../Tooltip";

async function isRunningInFirefox() {
    return navigator.userAgent.toLowerCase().includes("firefox");
}

export interface ItemPreviewFormik extends SourcePickerFormik {
    title: string;
    description: string;
    date: string;
    customImage?: string;
    style: UserCoverStyle | null;
    resetPreview: boolean;
    imageUpdated: boolean;
}

interface ItemPreviewEditProps extends Omit<ItemPreviewFormik, "newSource" | "customImage" | "resetPreview" | "imageUpdated"> {
    sourcesOptions: { value: string; text: string | null }[];
    sourceSlug: string;
    allowCreateNewSource?: boolean;
    previewImage?: string | null;
    isAddContentFlow?: boolean;
    onNextText: string;
    contentType: ContentType;
    mainParagraph?: JSX.Element;
    hidePreviewLabel?: boolean;
    isBulkImport?: boolean;
    isPlugin?: boolean;
    newSource?: boolean;
    pageUrl?: string;
    contentDetailType?: ContentDetailType;
    onTemporaryImageUploadHandler: (file: File) => Promise<string>;
    onItemPreviewSubmit: (data: ItemPreviewFormik) => Promise<void>;
    onBackHandler?: () => void;
    onSkip?: () => void;
}

export const ItemPreviewEdit = ({
    title,
    date,
    style,
    sourcesOptions,
    previewImage,
    description,
    allowCreateNewSource = false,
    newSource = false,
    isAddContentFlow = false,
    sourceSlug,
    onNextText,
    name,
    contentType,
    contentDetailType,
    hidePreviewLabel,
    mainParagraph,
    isBulkImport = false,
    isPlugin = false,
    onTemporaryImageUploadHandler,
    onItemPreviewSubmit,
    onBackHandler,
    onSkip,
    pageUrl
}: ItemPreviewEditProps) => {
    const [actionSubmitting, setActionSubmitting] = useState(false);
    const [isFF, setIsFF] = useState(false);

    const doesntHaveSources = !sourcesOptions.length;

    useEffect(() => {
        const checkFF = async () => {
            setIsFF(await isRunningInFirefox());
        };

        checkFF();
    }, []);

    const itemPreviewFormik = useFormik<ItemPreviewFormik>({
        initialValues: {
            name,
            sourceSlug,
            newSource: doesntHaveSources || newSource,
            title: title || "",
            description: description || "",
            date: !!date ? renderArticleDate(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
            customImage: previewImage || "",
            style,
            resetPreview: false,
            imageUpdated: false
        },
        onSubmit: async (data, formikHelpers) => {
            setActionSubmitting(true);

            try {
                await onItemPreviewSubmit(data);
                formikHelpers.resetForm();
            } catch {}

            setActionSubmitting(false);
        },
        validationSchema: Yup.object().shape({
            title: !isAddContentFlow ? Yup.string() : Yup.string().required("Please add a title"),
            description:
                !isAddContentFlow || !description.length
                    ? Yup.string()
                    : Yup.string().required("Please add a description").max(MAX_DESCRIPTION_SIZE, "Description must be 250 characters or less"),
            name: !isAddContentFlow ? Yup.string() : YupNameValidation,
            date: !isAddContentFlow ? Yup.string() : YupDateValidation,
            sourceSlug: !isAddContentFlow ? Yup.string() : YupSourceValidation
        }),
        enableReinitialize: true
    });

    const doesntHavePreview = style === "none" || (style === null && !previewImage);

    return (
        <>
            {isPlugin && (
                <VerticalSpacing bottom={14}>
                    <V3Title>Edit preview</V3Title>
                </VerticalSpacing>
            )}
            <V3PanelWithPreview
                title={itemPreviewFormik.values.title || (isAddContentFlow ? undefined : title)}
                description={itemPreviewFormik.values.description || (isAddContentFlow ? undefined : description)}
                imageSrc={itemPreviewFormik.values.customImage || ""}
                showMediaPreview
                showMetaContent
                date={itemPreviewFormik.values.date}
                contentType={contentType}
                contentDetailType={contentDetailType}
                hidePreviewLabel={hidePreviewLabel}
                isBulkImport={isBulkImport}
                sourceName={
                    itemPreviewFormik.values.newSource
                        ? itemPreviewFormik.values.name
                        : sourcesOptions.find((it) => it.value === itemPreviewFormik.values.sourceSlug)?.text || ""
                }
                hidePreviewTitle={isPlugin}
                mainPanel={
                    <>
                        <VerticalSpacing bottom={24}>
                            {!!mainParagraph && <VerticalSpacing bottom={14}>{mainParagraph}</VerticalSpacing>}
                            {isFF && isPlugin ? (
                                <BannerDetails useYellow customPadding="8px 12px">
                                    Firefox does not allow preview image editing.{" "}
                                    <FFNoticeTooltip>
                                        <V3Tooltip
                                            tooltipMaxWidth={350}
                                            placement="right"
                                            referenceContent={<V3IconHelpCircle />}
                                            tooltipContent={
                                                <>
                                                    You can edit the preview image once you've added the work sample to your account.{"  "}
                                                    <NotificationInlineLink
                                                        href="https://authory.com/help/en/articles/9667500-how-does-the-preview-of-content-items-work"
                                                        target="_blank"
                                                    >
                                                        Learn more
                                                    </NotificationInlineLink>
                                                </>
                                            }
                                        />
                                    </FFNoticeTooltip>
                                </BannerDetails>
                            ) : (
                                <MediaUploadWrapper
                                    disabled={actionSubmitting}
                                    onFileUploadHandler={async (selectorFiles) => {
                                        return await onTemporaryImageUploadHandler(selectorFiles[0]);
                                    }}
                                    onUploadSuccess={(filepath) => {
                                        itemPreviewFormik.setFieldValue("customImage", filepath);
                                        itemPreviewFormik.setFieldValue("style", UserCoverStyle.image);
                                        itemPreviewFormik.setFieldValue("imageUpdated", true);
                                    }}
                                    onRemovePreview={
                                        doesntHavePreview
                                            ? undefined
                                            : () => {
                                                  itemPreviewFormik.setFieldValue("customImage", "");
                                                  itemPreviewFormik.setFieldValue("style", UserCoverStyle.none);
                                                  itemPreviewFormik.setFieldValue("imageUpdated", true);
                                              }
                                    }
                                    hasPreviewImage={!!itemPreviewFormik.values.customImage?.length}
                                />
                            )}
                        </VerticalSpacing>
                        <form onSubmit={itemPreviewFormik.handleSubmit}>
                            <VerticalSpacing bottom={24}>
                                <V3TextInput
                                    label="Title"
                                    onChange={itemPreviewFormik.handleChange}
                                    onBlur={itemPreviewFormik.handleBlur}
                                    name="title"
                                    id="title"
                                    disabled={actionSubmitting}
                                    value={itemPreviewFormik.values.title}
                                    error={
                                        itemPreviewFormik.errors.hasOwnProperty("title") && itemPreviewFormik.touched.hasOwnProperty("title")
                                            ? itemPreviewFormik.errors.title
                                            : undefined
                                    }
                                />
                            </VerticalSpacing>
                            <VerticalSpacing bottom={24}>
                                <V3TextArea
                                    label="Description"
                                    tooltipMaxWidth={370}
                                    tooltipContent={
                                        <>
                                            The description appears in the Masonry, Stacked, List, and Text layouts, but not in Grid, which shows only
                                            the title and image. It&apos;s also used in the preview when you share this item, so it&apos;s worth
                                            filling in even if your portfolio uses Grid.
                                        </>
                                    }
                                    onChange={itemPreviewFormik.handleChange}
                                    onBlur={itemPreviewFormik.handleBlur}
                                    name="description"
                                    id="description"
                                    rows={2}
                                    disabled={actionSubmitting}
                                    value={itemPreviewFormik.values.description}
                                    characterLimit={MAX_DESCRIPTION_SIZE}
                                    error={
                                        itemPreviewFormik.errors.hasOwnProperty("description") &&
                                        itemPreviewFormik.touched.hasOwnProperty("description")
                                            ? itemPreviewFormik.errors.description
                                            : undefined
                                    }
                                />
                            </VerticalSpacing>
                            <ItemPreviewSourceDateWrapper>
                                <div>
                                    <SourcePicker
                                        formik={itemPreviewFormik}
                                        isAddContentFlow={isAddContentFlow}
                                        allowCreateNewSource={allowCreateNewSource}
                                        sourcesOptions={sourcesOptions}
                                        actionSubmitting={actionSubmitting}
                                        pageUrl={pageUrl}
                                    />
                                </div>
                                <div>
                                    <ItemPreviewDateInputWrapper>
                                        <V3TextInput
                                            label="Date"
                                            tooltipContent={
                                                <>
                                                    All content items in your Authory account are ordered by date by default. You can switch to
                                                    ordering manually anytime.{" "}
                                                    <NotificationInlineLink
                                                        href="https://authory.com/help/en/articles/9667492-why-do-all-content-items-in-my-authory-require-a-date"
                                                        target="_blank"
                                                    >
                                                        Learn more
                                                    </NotificationInlineLink>
                                                </>
                                            }
                                            tooltipMaxWidth={280}
                                            type="date"
                                            disabled={actionSubmitting}
                                            onChange={itemPreviewFormik.handleChange}
                                            onBlur={itemPreviewFormik.handleBlur}
                                            name="date"
                                            id="date"
                                            value={itemPreviewFormik.values.date}
                                            error={
                                                itemPreviewFormik.errors.hasOwnProperty("date") && itemPreviewFormik.touched.hasOwnProperty("date")
                                                    ? itemPreviewFormik.errors.date
                                                    : undefined
                                            }
                                        />
                                    </ItemPreviewDateInputWrapper>
                                </div>
                            </ItemPreviewSourceDateWrapper>
                        </form>
                    </>
                }
                btnPanel={
                    <VerticalSpacing top={24}>
                        <ItemPreviewBtnWrapper>
                            <div>
                                {itemPreviewFormik.dirty &&
                                    isAddContentFlow &&
                                    (itemPreviewFormik.initialValues.title !== itemPreviewFormik.values.title ||
                                        itemPreviewFormik.initialValues.customImage !== itemPreviewFormik.values.customImage ||
                                        itemPreviewFormik.initialValues.description !== itemPreviewFormik.values.description) &&
                                    (itemPreviewFormik.initialValues.title !== "" ||
                                        itemPreviewFormik.initialValues.customImage !== "" ||
                                        itemPreviewFormik.initialValues.customImage !== "") &&
                                    !actionSubmitting && (
                                        <V3TertiaryButton
                                            ctaColor={V3TertiaryCTAColor.gray}
                                            icon={<V3IconRefresh />}
                                            type="button"
                                            text="Revert to default preview"
                                            onClick={async () => {
                                                if (isAddContentFlow) {
                                                    itemPreviewFormik.resetForm();
                                                } else {
                                                    itemPreviewFormik.setFieldValue("resetPreview", true);
                                                    itemPreviewFormik.submitForm();
                                                }
                                            }}
                                        />
                                    )}
                            </div>
                            <RightAlignPanel>
                                {onBackHandler && (
                                    <div>
                                        <V3Button
                                            text={"Back"}
                                            disabled={actionSubmitting}
                                            autoWidth
                                            onClick={onBackHandler}
                                            color={V3ButtonColor.secondary}
                                        />
                                    </div>
                                )}
                                {onSkip && (
                                    <div>
                                        <V3Button
                                            text={"Skip"}
                                            disabled={actionSubmitting}
                                            autoWidth
                                            onClick={onSkip}
                                            color={V3ButtonColor.secondary}
                                        />
                                    </div>
                                )}
                                <div>
                                    {actionSubmitting ? (
                                        <V3Loader height={42} />
                                    ) : (
                                        <V3Button
                                            text={onNextText}
                                            type="submit"
                                            disabled={actionSubmitting}
                                            autoWidth
                                            onClick={() => {
                                                itemPreviewFormik.submitForm();
                                            }}
                                        />
                                    )}
                                </div>
                            </RightAlignPanel>
                        </ItemPreviewBtnWrapper>
                    </VerticalSpacing>
                }
            />
        </>
    );
};
